import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import Button from "../Button";
import { Typography } from "../Typography";
import TextField from "../TextField";
import { createIdea, updateIdea, getAdminDropdownOptions, uploadIdeaImage, inviteTeamMember, getAllConfigurations } from "../../Utils/apiServices";
import { stripNumbersFromName } from "../../Utils/nameValidation";
import { getHttpUrlError } from "../../Utils/urlValidation";
import { useAuth } from "../../Context/authContext";
import AddTeamMemberDialog from "../../Container/EngagementDetail/AddTeamMemberDialog";
import toast from "../../Utils/toastManager";

const parseInnovationCategoryOptions = (res) => {
    if (!res?.success || res?.data === undefined) return [];
    const payload = res.data?.data ?? res.data;
    const list = Array.isArray(payload) ? payload : (payload?.options ?? []);
    return (Array.isArray(list) ? list : [])
        .filter((o) => o && (o?.is_active !== false && o?.enabled !== false))
        .sort((a, b) => (a?.display_order ?? 0) - (b?.display_order ?? 0))
        .map((o) => ({ value: String(o?.value ?? o?.label ?? ""), label: String(o?.value ?? o?.label ?? "") }))
        .filter((o) => o.value);
};

const MAX_INNOVATION_CATEGORY_SELECT = 3;

const CreateIdea = ({ onSubmit, onDraftSaved, initialIdea = null, isSubmitDisabled = false, submitDisabledReason = "" }) => {
    const { user } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    // Id of the draft being edited/continued (null for a brand-new idea). Once a
    // draft is saved, this is set so subsequent saves/submits update the same row.
    const [draftId, setDraftId] = useState(initialIdea?.id ?? null);
    const [submitError, setSubmitError] = useState("");
    const [innovationCategoryOptions, setInnovationCategoryOptions] = useState([]);
    const [loadingInnovationCategories, setLoadingInnovationCategories] = useState(true);
    const [formData, setFormData] = useState({
        ideaTitle: "",
        innovationCategory: [],
        problemStatement: "",
        proposedSolution: "",
        targetAudience: "",
        existingAlternatives: "",
        primaryImpact: "",
        participationMode: "solo",
        teamSize: "",
        founderFirstName: "",
        founderLastName: "",
        conceptDiagram: "",
        ideaVideoLink: "",
        otherDocuments: "",
        ideaImage: "",
    });

    const [errors, setErrors] = useState({});
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imageUploadError, setImageUploadError] = useState("");
    const [isDraggingIdeaImage, setIsDraggingIdeaImage] = useState(false);
    // Team members collected before submission; their student accounts are
    // created and linked as SECONDARY_STUDENT after the idea is created.
    const [teamMembers, setTeamMembers] = useState([]);
    const [showAddTeamMember, setShowAddTeamMember] = useState(false);
    // Max total students per idea (incl. the founder), from admin configuration.
    // Falls back to the backend default (4) until the config loads.
    const [studentLimit, setStudentLimit] = useState(4);

    // Secondary members allowed = team size minus the founder, capped by the
    // configured per-idea student limit.
    const selectedTeamSize = formData.teamSize ? parseInt(formData.teamSize) : studentLimit;
    const maxTeamMembers = Math.max(0, Math.min(selectedTeamSize, studentLimit) - 1);

    const removeTeamMember = (index) => {
        setTeamMembers((prev) => prev.filter((_, i) => i !== index));
    };

    useEffect(() => {
        const fetchInnovationCategories = async () => {
            try {
                setLoadingInnovationCategories(true);
                const res = await getAdminDropdownOptions("industry_experience", true);
                setInnovationCategoryOptions(parseInnovationCategoryOptions(res));
            } catch (err) {
                console.error("Error loading innovation category options:", err);
                setInnovationCategoryOptions([]);
            } finally {
                setLoadingInnovationCategories(false);
            }
        };
        fetchInnovationCategories();
    }, []);

    // Load the per-idea student limit from the admin configuration.
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await getAllConfigurations();
                const payload = res?.data?.data ?? res?.data;
                const config = Array.isArray(payload) ? payload[0] : payload;
                const students = Number(config?.student_per_idea);
                if (active && Number.isFinite(students) && students > 0) {
                    setStudentLimit(students);
                }
            } catch (err) {
                console.error("Error loading per-idea student limit:", err);
            }
        })();
        return () => {
            active = false;
        };
    }, []);

    // Prefill the form when continuing a saved draft (or editing an existing idea).
    useEffect(() => {
        if (!initialIdea) return;
        setDraftId(initialIdea.id ?? null);
        setFormData((prev) => ({
            ...prev,
            ideaTitle: initialIdea.title || "",
            innovationCategory: initialIdea.innovation_category
                ? String(initialIdea.innovation_category)
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                : [],
            problemStatement: initialIdea.problem_statement || "",
            proposedSolution: initialIdea.proposed_solution || "",
            targetAudience: initialIdea.target_audience || "",
            existingAlternatives: initialIdea.existing_alternatives || "",
            primaryImpact: initialIdea.primary_impact || "",
            participationMode: initialIdea.participation_mode || "solo",
            teamSize: initialIdea.team_size ? String(initialIdea.team_size) : "",
            founderFirstName: initialIdea.founder_first_name || "",
            founderLastName: initialIdea.founder_last_name || "",
            conceptDiagram: initialIdea.concept_diagram || "",
            ideaVideoLink: initialIdea.idea_video_link || "",
            otherDocuments: initialIdea.other_document || "",
            ideaImage: initialIdea.idea_image || "",
        }));
    }, [initialIdea]);

    // Build the API payload from the current form state (shared by draft save and
    // final submit).
    const buildApiPayload = () => {
        const payload = {
            title: formData.ideaTitle.trim(),
            innovation_category: Array.isArray(formData.innovationCategory)
                ? formData.innovationCategory.join(",")
                : (formData.innovationCategory || ""),
            problem_statement: formData.problemStatement.trim(),
            proposed_solution: formData.proposedSolution.trim(),
            target_audience: formData.targetAudience.trim(),
            existing_alternatives: formData.existingAlternatives.trim(),
            primary_impact: formData.primaryImpact,
            participation_mode: formData.participationMode,
            founder_first_name: formData.founderFirstName.trim(),
            founder_last_name: formData.founderLastName.trim(),
            concept_diagram: formData.conceptDiagram.trim() || null,
            idea_video_link: formData.ideaVideoLink.trim() || null,
            other_document: formData.otherDocuments.trim() || null,
            idea_image: formData.ideaImage?.trim() || null,
            // Lets the backend enforce per-student idea limits and decide whether
            // the idea starts Pending or Inactive.
            user_id: user?.id ?? null,
        };
        if (formData.participationMode === "team" && formData.teamSize) {
            payload.team_size = parseInt(formData.teamSize);
        }
        return payload;
    };

    // Save the current form as a draft (create the first time, update thereafter).
    // Only the title is required so a partially-filled idea can be parked and
    // finished later.
    const handleSaveDraft = async () => {
        if (!formData.ideaTitle.trim()) {
            setErrors((prev) => ({ ...prev, ideaTitle: "Idea Title is required to save a draft" }));
            setSubmitError("Please add an Idea Title before saving your draft.");
            return;
        }
        try {
            setSavingDraft(true);
            setSubmitError("");
            const apiPayload = buildApiPayload();
            apiPayload.status = "DRAFT";

            const response = draftId
                ? await updateIdea(draftId, apiPayload)
                : await createIdea(apiPayload);

            if (response.success) {
                const savedIdea =
                    response.data?.data ?? response.data ?? null;
                const savedId = savedIdea?.id ?? draftId;
                const isNew = !draftId;
                if (savedId && isNew) setDraftId(savedId);
                if (onDraftSaved) onDraftSaved(response, { isNew, ideaId: savedId });
            } else {
                setSubmitError(response.error || "Failed to save draft. Please try again.");
            }
        } catch (error) {
            setSubmitError(error.error || "An error occurred while saving the draft. Please try again.");
        } finally {
            setSavingDraft(false);
        }
    };

    const handleInputChange = (field, value) => {
            if (field === "founderFirstName" || field === "founderLastName") {
                value = stripNumbersFromName(value);
            }
        setFormData((prev) => {
            const updated = { ...prev, [field]: value };
            // Clear teamSize when switching to solo mode
            if (field === "participationMode" && value === "solo") {
                updated.teamSize = "";
            }
            return updated;
        });
        // Team members only apply to team mode.
        if (field === "participationMode" && value === "solo") {
            setTeamMembers([]);
        }
        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (isDraft = false) => {
        if (!isDraft && isSubmitDisabled) {
            setSubmitError(submitDisabledReason || "You cannot submit a new idea right now.");
            return;
        }

        const newErrors = {};

        if (!isDraft) {
            if (!formData.ideaTitle.trim()) newErrors.ideaTitle = "Idea Title is required";
            const cat = formData.innovationCategory;
            if (!Array.isArray(cat) || cat.length === 0) newErrors.innovationCategory = "Innovation Category is required";
            else if (cat.length > MAX_INNOVATION_CATEGORY_SELECT) newErrors.innovationCategory = `Maximum ${MAX_INNOVATION_CATEGORY_SELECT} categories allowed`;
            if (!formData.problemStatement.trim()) newErrors.problemStatement = "Problem Statement is required";
            if (!formData.proposedSolution.trim()) newErrors.proposedSolution = "Proposed Solution is required";
            if (!formData.targetAudience.trim()) newErrors.targetAudience = "Target Audience is required";
            if (!formData.existingAlternatives.trim()) newErrors.existingAlternatives = "Existing Alternatives is required";
            if (!formData.primaryImpact) newErrors.primaryImpact = "Primary Impact is required";
            if (formData.participationMode === "team" && !formData.teamSize) {
                newErrors.teamSize = "Team Size is required";
            }
            if (formData.participationMode === "team") {
                if (!formData.founderFirstName.trim()) newErrors.founderFirstName = "First Name is required";
                if (!formData.founderLastName.trim()) newErrors.founderLastName = "Last Name is required";
                if (teamMembers.length > maxTeamMembers) {
                    newErrors.teamMembers = `Your team size allows up to ${maxTeamMembers} team member${maxTeamMembers === 1 ? "" : "s"} besides you. Remove some members or pick a bigger team size.`;
                }
            }
            const conceptDiagramError = getHttpUrlError(formData.conceptDiagram, {
                fieldLabel: "Concept Diagram",
            });
            if (conceptDiagramError) newErrors.conceptDiagram = conceptDiagramError;
            const ideaVideoLinkError = getHttpUrlError(formData.ideaVideoLink, {
                fieldLabel: "Video Link",
            });
            if (ideaVideoLinkError) newErrors.ideaVideoLink = ideaVideoLinkError;
            const otherDocumentsError = getHttpUrlError(formData.otherDocuments, {
                fieldLabel: "Addition Documents",
            });
            if (otherDocumentsError) newErrors.otherDocuments = otherDocumentsError;
        }

        setErrors(newErrors);
        setSubmitError("");

        if (Object.keys(newErrors).length === 0) {
                // Submit to API
                try {
                    setSubmitting(true);

                    const apiPayload = buildApiPayload();

                    // Igniting a saved draft updates that same idea and moves it
                    // out of DRAFT into review; a fresh idea is created directly.
                    const response = draftId
                        ? await updateIdea(draftId, { ...apiPayload, status: "PENDING" })
                        : await createIdea(apiPayload);

                    if (response.success) {
                        // Email an invitation to each collected team member. Their
                        // accounts are created when they accept and they're linked
                        // to the idea as view-only SECONDARY_STUDENTs.
                        const newIdeaId =
                            response.data?.data?.id ?? response.data?.id ?? draftId ?? null;
                        if (newIdeaId && teamMembers.length > 0) {
                            const failedMembers = [];
                            for (const member of teamMembers) {
                                try {
                                    const inviteRes = await inviteTeamMember({
                                        idea_id: newIdeaId,
                                        invited_by: user?.id,
                                        email: member.email,
                                        first_name: member.first_name,
                                        last_name: member.last_name,
                                    });
                                    if (!inviteRes?.success) failedMembers.push(member.email);
                                } catch (memberError) {
                                    console.error("Error inviting team member:", memberError);
                                    failedMembers.push(member.email);
                                }
                            }
                            if (failedMembers.length > 0) {
                                toast.error(
                                    `Your idea was submitted, but these team members could not be invited: ${failedMembers.join(", ")}. You can invite them later from the Collaboration tab.`
                                );
                            }
                        }
                        setTeamMembers([]);
                        // Call onSubmit callback if provided. fromDraft tells the
                        // parent the idea-user link already exists (created when the
                        // draft was first saved), so it shouldn't create it again.
                        if (onSubmit) {
                            onSubmit(formData, response, { fromDraft: Boolean(draftId) });
                        }
                        setDraftId(null);
                        // Reset form on success
                        setFormData({
                            ideaTitle: "",
                            innovationCategory: [],
                            problemStatement: "",
                            proposedSolution: "",
                            targetAudience: "",
                            existingAlternatives: "",
                            primaryImpact: "",
                            participationMode: "solo",
                            teamSize: "",
                            founderFirstName: "",
                            founderLastName: "",
                            conceptDiagram: "",
                            ideaVideoLink: "",
                            otherDocuments: "",
                            ideaImage: "",
                        });
                        setErrors({});
                        setImageUploadError("");
                    } else {
                        setSubmitError(response.error || "Failed to submit idea. Please try again.");
                    }
                } catch (error) {
                    setSubmitError(error.error || "An error occurred while submitting the idea. Please try again.");
                } finally {
                    setSubmitting(false);
                }
        }
    };

    const getWordCount = (text) => {
        return text.trim().split(/\s+/).filter(word => word.length > 0).length;
    };

    const handleIdeaImageUpload = async (file) => {
        if (!file || !file.type.startsWith("image/")) {
            setImageUploadError("Please select an image file (PNG, JPG, GIF, etc.).");
            return;
        }
        setImageUploadError("");
        setUploadingImage(true);
        try {
            const uploadResponse = await uploadIdeaImage(file);
            let uploadData = uploadResponse?.data;
            if (uploadData?.data?.data) uploadData = uploadData.data;
            const imageUrl = uploadData?.data?.url ?? uploadData?.url ?? uploadData?.data?.data?.url;
            if (uploadResponse.success && imageUrl) {
                handleInputChange("ideaImage", imageUrl);
            } else {
                setImageUploadError(uploadResponse?.error || uploadData?.error || "Failed to upload image. Please try again.");
            }
        } catch (err) {
            setImageUploadError(err?.error || "Failed to upload image. Please try again.");
        } finally {
            setUploadingImage(false);
        }
    };

    const handleIdeaImageDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingIdeaImage(false);
        const file = e.dataTransfer?.files?.[0];
        if (file) handleIdeaImageUpload(file);
    };

    return (
        <div className="flex flex-col w-full h-auto gap-8 bg-primary px-8 py-8">
            {/* Header Section */}
            <div className="flex items-start justify-between w-full">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between w-full gap-2">
                    <div className="flex flex-col gap-2 items-start justify-start">
                        <Typography as="h1" className="text-2xl font-bold text-primary-900">
                            {draftId ? "Continue Your Spark" : "Fuel Your Spark"}
                        </Typography>
                        <Typography as="p" className="text-sm text-tertiary-600">
                            {draftId
                                ? "Pick up where you left off. Save again to keep it as a draft, or ignite it when you're ready."
                                : "Share the details of your innovation and let's build the blueprint for your future."}
                        </Typography>
                    </div>
                    <div className="flex gap-3">
                        <Button
                            variant="secondary"
                            className="bg-white text-[#414651] border border-[#D5D7DA] text-sm font-medium rounded-lg"
                            size="medium"
                            onClick={handleSaveDraft}
                            disabled={submitting || savingDraft}
                        >
                            {savingDraft ? "Saving..." : "Save for Later"}
                        </Button>
                        <Button
                            variant="primary"
                            className="bg-[#7F56D9] hover:bg-purple-700 text-white border-2 border-[#FFFFFF] text-sm font-medium rounded-lg"
                            size="medium"
                            onClick={() => handleSubmit(false)}
                            disabled={submitting || savingDraft || isSubmitDisabled}
                        >
                            {submitting ? "Submitting..." : "Ignite Your Idea"}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Form Sections */}
            <div className="flex flex-col gap-8 border-t border-slate-200 pt-8">
                {/* Section 1: Idea Identity */}
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1 text-start w-full border-b border-slate-200 pb-4">
                        <Typography as="h2" className="text-xl font-semibold text-primary-900">
                            The Core Identity
                        </Typography>
                        <Typography as="p" className="text-sm text-tertiary-600">
                            Give your idea a name and define the challenge you are choosing to solve
                        </Typography>
                    </div>
                    <div className="flex flex-col gap-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                <div className="flex items-center gap-1">
                                    Idea Title <span className="text-red-500">*</span>
                                </div>
                                <span className="text-xs text-tertiary-600">What is your Spark called?</span>
                            </label>
                            <TextField
                                name="ideaTitle"
                                value={formData.ideaTitle}
                                onChange={(e) => handleInputChange("ideaTitle", e.target.value)}
                                placeholder="Enter a catchy name for the Innovation."
                                colSpan={2}
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                <div className="flex items-center gap-1">
                                    Innovation Category <span className="text-red-500">*</span>
                                </div>
                                <span className="text-xs text-tertiary-600">Which field does your solution disrupt?</span>
                            </label>
                            <div className="col-span-2 flex flex-col gap-1">
                                <div className="relative">
                                    <select
                                        multiple
                                        value={Array.isArray(formData.innovationCategory) ? formData.innovationCategory : []}
                                        onChange={(e) => {
                                            const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                                            handleInputChange("innovationCategory", selected.slice(0, MAX_INNOVATION_CATEGORY_SELECT));
                                        }}
                                        disabled={loadingInnovationCategories}
                                        className={`w-full rounded-lg border ${errors.innovationCategory ? "border-red-400" : "border-slate-200"} bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue min-h-[100px] appearance-none disabled:opacity-70`}
                                    >
                                        {innovationCategoryOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                    <Icon
                                        icon="lucide:chevron-down"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                                    />
                                </div>
                                <p className="text-xs text-slate-500">
                                    {loadingInnovationCategories ? "Loading categories..." : `Hold Ctrl/Cmd to select up to ${MAX_INNOVATION_CATEGORY_SELECT} (${(formData.innovationCategory || []).length} selected)`}
                                </p>
                                {Array.isArray(formData.innovationCategory) && formData.innovationCategory.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {formData.innovationCategory.map((item) => (
                                            <span key={item} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 text-blue-700 text-xs font-medium">
                                                {item}
                                                <button
                                                    type="button"
                                                    onClick={() => handleInputChange("innovationCategory", formData.innovationCategory.filter((x) => x !== item))}
                                                    className="hover:text-blue-900"
                                                    aria-label="Remove"
                                                >
                                                    <Icon icon="lucide:x" className="h-3 w-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {errors.innovationCategory && (
                                    <p className="text-xs text-red-500 mt-1">{errors.innovationCategory}</p>
                                )}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                <div className="flex items-center gap-1">
                                Problem Statement <span className="text-red-500">*</span>
                                </div>
                                <span className="text-xs text-tertiary-600">
                                    What is the specific pain point or gap you've identified?
                                </span>
                            </label>
                            <TextField
                                name="problemStatement"
                                value={formData.problemStatement}
                                onChange={(e) => handleInputChange("problemStatement", e.target.value)}
                                placeholder="What specific pain points are you solving? Who is the victim of this problem? Write those here."
                                multiline
                                rows={6}
                                error={errors.problemStatement}
                                colSpan={2}
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                <div className="flex items-center gap-1">
                                    Proposed Solution <span className="text-red-500">*</span>
                                </div>
                                <span className="text-xs text-tertiary-600">
                                    In simple terms, how does your Spark resolve the problem?
                                </span>
                            </label>
                            <TextField
                                name="proposedSolution"
                                value={formData.proposedSolution}
                                onChange={(e) => handleInputChange("proposedSolution", e.target.value)}
                                placeholder="How does your idea solve the problem? What is the unique value proposition?"
                                multiline
                                rows={8}
                                error={errors.proposedSolution}
                                colSpan={2}
                            />
                        </div>
                    </div>
                </div>

                {/* Section 2: Market Impact */}
                <div className="flex flex-col gap-4 w-full border-t border-slate-200 pt-8">
                    <div className="flex flex-col gap-1 text-start border-b border-slate-200 pb-4">
                        <Typography as="h2" className="text-xl font-semibold text-primary-900">
                            Impact & Market Landscape
                        </Typography>
                        <Typography as="p" className="text-sm text-tertiary-600">
                            Define who benefits from your solution and how you stand out from the crowd.
                        </Typography>
                    </div>
                    <div className="flex flex-col gap-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                <div className="flex items-center gap-1">
                                    Target Audience <span className="text-red-500">*</span>
                                </div>
                                <span className="text-xs text-tertiary-600">Who are the primary users or beneficiaries of this innovation?</span>
                            </label>
                            <TextField
                                name="targetAudience"
                                value={formData.targetAudience}
                                onChange={(e) => handleInputChange("targetAudience", e.target.value)}
                                placeholder="Select the target audience (multiple if applicable)."
                                error={errors.targetAudience}
                                colSpan={2}
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                <div className="flex items-center gap-1">
                                    Existing Alternatives <span className="text-red-500">*</span>
                                </div>
                                <span className="text-xs text-tertiary-600">
                                    How is this problem being addressed today, and why is your way better?
                                </span>
                            </label>
                            <TextField
                                name="existingAlternatives"
                                value={formData.existingAlternatives}
                                onChange={(e) => handleInputChange("existingAlternatives", e.target.value)}
                                placeholder="How are people solving this problem currently? What is your competition? List them here."
                                multiline
                                rows={8}
                                error={errors.existingAlternatives}
                                colSpan={2}
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                <div className="flex items-center gap-1">
                                    Primary Impact <span className="text-red-500">*</span>
                                </div>
                                <span className="text-xs text-tertiary-600">What is the single biggest change your idea will create in the real world?</span>
                            </label>
                            <div className="relative col-span-2">
                                <select
                                    value={formData.primaryImpact}
                                    onChange={(e) => handleInputChange("primaryImpact", e.target.value)}
                                    className={`w-full rounded-lg border ${errors.primaryImpact ? "border-red-400" : "border-slate-200"} bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none`}
                                >
                                    <option value="">What is the primary impact as a result of your idea?</option>
                                    <option value="Social Impact">Social Impact</option>
                                    <option value="Environmental Impact">Environmental Impact</option>
                                    <option value="Economic Impact">Economic Impact</option>
                                    <option value="Educational Impact">Educational Impact</option>
                                    <option value="Healthcare Impact">Healthcare Impact</option>
                                    <option value="Other">Other</option>
                                </select>
                                <Icon
                                    icon="lucide:chevron-down"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                                />
                            </div>
                            {errors.primaryImpact && (
                                <p className="text-xs text-red-500 mt-1">{errors.primaryImpact}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Section 3: Team */}
                <div className="flex flex-col gap-4 w-full border-t border-slate-200 pt-8">
                    <div className="flex flex-col gap-1 text-start border-b border-slate-200 pb-4">
                        <Typography as="h2" className="text-xl font-semibold text-primary-900">
                            Collaboration Mode
                        </Typography>
                        <Typography as="p" className="text-sm text-tertiary-600">
                            Choose how you want to bring this Spark to life.
                        </Typography>
                    </div>
                    <div className="flex flex-col gap-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                <div className="flex items-center gap-1">
                                    Participation Mode <span className="text-red-500">*</span>
                                </div>
                            </label>
                            <div className="flex col-span-2 gap-6">
                                <label className={`flex items-center gap-2 cursor-pointer border rounded-lg p-2 transition-colors ${
                                    formData.participationMode === "solo" 
                                        ? "border-[#7F56D9] bg-purple-50" 
                                        : "border-[#E9EAEB]"
                                }`}>
                                    <input
                                        type="radio"
                                        name="participationMode"
                                        value="solo"
                                        checked={formData.participationMode === "solo"}
                                        onChange={(e) => handleInputChange("participationMode", e.target.value)}
                                        className="w-4 h-4 text-brand-blue border-slate-300 focus:ring-brand-blue"
                                    />
                                    <div className="flex flex-col text-start">
                                        <span className="text-sm font-medium text-slate-700">Solo</span>
                                        <span className="text-xs text-tertiary-600">I am building this Spark independently.</span>
                                    </div>
                                </label>
                                <label className={`flex items-center gap-2 cursor-pointer border rounded-lg p-2 transition-colors ${
                                    formData.participationMode === "team" 
                                        ? "border-[#7F56D9] bg-purple-50" 
                                        : "border-[#E9EAEB]"
                                }`}>
                                    <input
                                        type="radio"
                                        name="participationMode"
                                        value="team"
                                        checked={formData.participationMode === "team"}
                                        onChange={(e) => handleInputChange("participationMode", e.target.value)}
                                        className="w-4 h-4 text-brand-blue border-slate-300 focus:ring-brand-blue"
                                    />
                                    <div className="flex flex-col text-start">
                                        <span className="text-sm font-medium text-slate-700">Team</span>
                                        <span className="text-xs text-tertiary-600">I am collaborating with a group of innovators.</span>
                                    </div>
                                </label>
                            </div>
                        </div>
                        {formData.participationMode === "team" && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                    <div className="flex items-center gap-1">
                                        Team Size <span className="text-red-500">*</span>
                                    </div>
                                </label>
                                <div className="relative col-span-2">
                                    <select
                                        value={formData.teamSize}
                                        onChange={(e) => handleInputChange("teamSize", e.target.value)}
                                        className={`w-full rounded-lg border ${errors.teamSize ? "border-red-400" : "border-slate-200"} bg-white px-4 py-2.5 pr-10 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none`}
                                    >
                                        <option value="">
                                            {`Select a range between 2-${studentLimit} students`}
                                        </option>
                                        {Array.from(
                                            { length: Math.max(0, studentLimit - 1) },
                                            (_, i) => i + 2
                                        ).map((n) => (
                                            <option key={n} value={String(n)}>
                                                {n} students
                                            </option>
                                        ))}
                                    </select>
                                    <Icon
                                        icon="lucide:chevron-down"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                                    />
                                </div>
                                {errors.teamSize && (
                                    <p className="text-xs text-red-500 mt-1 col-span-2">{errors.teamSize}</p>
                                )}
                            </div>
                        )}
                        {formData.participationMode === "team" && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                    <div className="flex items-center gap-1">
                                        Founder Name <span className="text-red-500">*</span>
                                    </div>
                                </label>
                                <TextField
                                    name="founderFirstName"
                                    value={formData.founderFirstName}
                                    onChange={(e) => handleInputChange("founderFirstName", e.target.value)}
                                    placeholder="First Name"
                                    required
                                    error={errors.founderFirstName}
                                />
                                <TextField
                                    name="founderLastName"
                                    value={formData.founderLastName}
                                    onChange={(e) => handleInputChange("founderLastName", e.target.value)}
                                    placeholder="Last Name"
                                    required
                                    error={errors.founderLastName}
                                />
                            </div>
                        )}
                        {formData.participationMode === "team" && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                    <div className="flex items-center gap-1">Team Members</div>
                                    <span className="text-xs text-tertiary-600">
                                        Add your team members by name and email. When you submit the idea, we'll email each of them an invite to set up their own account with view-only access.
                                    </span>
                                </label>
                                <div className="col-span-2 flex flex-col gap-3">
                                    {teamMembers.length > 0 && (
                                        <div className="flex flex-col gap-2">
                                            {teamMembers.map((member, index) => (
                                                <div
                                                    key={`${member.email}-${index}`}
                                                    className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5"
                                                >
                                                    <div className="flex flex-col text-left min-w-0">
                                                        <span className="text-sm font-medium text-slate-800 truncate">
                                                            {member.first_name} {member.last_name}
                                                        </span>
                                                        <span className="text-xs text-slate-500 truncate">
                                                            {member.email}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeTeamMember(index)}
                                                        className="text-slate-400 hover:text-red-500 transition-colors shrink-0"
                                                        title="Remove team member"
                                                    >
                                                        <Icon icon="lucide:trash-2" className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div>
                                        <Button
                                            variant="secondary"
                                            size="small"
                                            type="button"
                                            startIcon={<Icon icon="lucide:plus" className="h-4 w-4" />}
                                            onClick={() => setShowAddTeamMember(true)}
                                            disabled={teamMembers.length >= maxTeamMembers}
                                            title={
                                                teamMembers.length >= maxTeamMembers
                                                    ? "You've reached the team member limit for the selected team size."
                                                    : undefined
                                            }
                                        >
                                            Add Team Member
                                        </Button>
                                    </div>
                                    {errors.teamMembers && (
                                        <p className="text-xs text-red-500">{errors.teamMembers}</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Section 4: Attachments */}
                <div className="flex flex-col gap-4 w-full border-t border-slate-200 pt-8">
                    <div className="flex flex-col gap-1 text-start border-b border-slate-200 pb-4">
                        <Typography as="h2" className="text-xl font-semibold text-primary-900">
                            Artifacts & Visualizations
                        </Typography>
                        <Typography as="p" className="text-sm text-tertiary-600">
                            Share the materials you've crafted to help mentors visualize and verify your concept.
                        </Typography>
                    </div>
                    <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                        <Icon icon="lucide:info" className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                            Please do not use password-protected links for your concept diagram, video, or additional documents. Mentors must be able to open and review all shared materials without signing in or entering a password.
                        </span>
                    </div>
                    <div className="flex flex-col gap-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                <div className="flex items-center gap-1">
                                    Idea Image
                                </div>
                                <span className="text-xs text-tertiary-600">Upload a high-quality cover image to represent your Spark across the platform.</span>
                            </label>
                            <div className="col-span-2 flex flex-col gap-2">
                                {formData.ideaImage ? (
                                    <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                                        <img src={formData.ideaImage} alt="Idea" className="h-16 w-16 object-cover rounded" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-slate-600 truncate">Image uploaded</p>
                                            <p className="text-xs text-slate-500 truncate">{formData.ideaImage}</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleInputChange("ideaImage", "")}
                                            className="text-slate-500 hover:text-red-600 p-1"
                                            aria-label="Remove image"
                                        >
                                            <Icon icon="lucide:x" className="h-4 w-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <label
                                        className={`flex flex-col items-center justify-center w-full min-h-[120px] border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                                            isDraggingIdeaImage ? "border-purple-500 bg-purple-50" : "border-slate-200 bg-slate-50 hover:bg-slate-100"
                                        } ${uploadingImage ? "pointer-events-none opacity-70" : ""}`}
                                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingIdeaImage(true); }}
                                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDraggingIdeaImage(false); }}
                                        onDrop={handleIdeaImageDrop}
                                    >
                                        <div className="flex flex-col items-center justify-center py-6 px-4">
                                            <Icon icon="lucide:upload" className="h-8 w-8 text-slate-400 mb-2" />
                                            <span className="text-sm text-slate-600">
                                                {uploadingImage ? "Uploading..." : "Click to upload or drag and drop"}
                                            </span>
                                            <span className="text-xs text-slate-500 mt-1">PNG, JPG or GIF</span>
                                        </div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) handleIdeaImageUpload(file);
                                                e.target.value = "";
                                            }}
                                        />
                                    </label>
                                )}
                                {imageUploadError && <p className="text-xs text-red-500">{imageUploadError}</p>}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                <div className="flex items-center gap-1">
                                    Concept Diagram <span className="text-red-500">*</span>
                                </div>
                                <span className="text-xs text-tertiary-600">Share a flowchart, architectural sketch, or wireframe of your solution.</span>
                            </label>
                            <TextField
                                name="conceptDiagram"
                                type="url"
                                value={formData.conceptDiagram}
                                onChange={(e) => handleInputChange("conceptDiagram", e.target.value)}
                                placeholder="Enter a URL here."
                                error={errors.conceptDiagram}
                                helperText={errors.conceptDiagram}
                                colSpan={2}
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                <div className="flex items-center gap-1">
                                    Video Link <span className="text-red-500">*</span>
                                </div>
                                <span className="text-xs text-tertiary-600">Provide a URL for a demo or pitch video (YouTube/Vimeo/Loom).</span>
                            </label>
                            <TextField
                                name="ideaVideoLink"
                                type="url"
                                value={formData.ideaVideoLink}
                                onChange={(e) => handleInputChange("ideaVideoLink", e.target.value)}
                                placeholder="Enter a video URL here."
                                error={errors.ideaVideoLink}
                                helperText={errors.ideaVideoLink}
                                colSpan={2}
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            <label className="flex flex-col gap-1 col-span-1 text-sm font-medium text-slate-700 mb-2 text-left">
                                <div className="flex items-center gap-1">
                                    Addition Documents <span className="text-red-500">*</span>
                                </div>
                                <span className="text-xs text-tertiary-600">Upload any whitepapers, research findings, or surveys created to validate the idea.</span>
                            </label>
                            <TextField
                                name="otherDocuments"
                                type="url"
                                value={formData.otherDocuments}
                                onChange={(e) => handleInputChange("otherDocuments", e.target.value)}
                                placeholder="Enter a URL here."
                                error={errors.otherDocuments}
                                helperText={errors.otherDocuments}
                                colSpan={2}
                            />
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {submitError && (
                    <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                        <Icon icon="lucide:alert-circle" className="h-4 w-4" />
                        <span>{submitError}</span>
                    </div>
                )}

                {/* Bottom Action Buttons */}
                <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                    <Button
                        variant="secondary"
                        size="medium"
                        className="bg-white text-[#414651] border border-[#D5D7DA] text-sm font-medium rounded-lg"
                        onClick={handleSaveDraft}
                        disabled={submitting || savingDraft}
                    >
                        {savingDraft ? "Saving..." : "Save for Later"}
                    </Button>
                    <Button
                        size="medium"
                        className="bg-[#7F56D9] hover:bg-purple-700 text-white border-2 border-[#FFFFFF] text-sm font-medium rounded-lg"
                        onClick={() => handleSubmit(false)}
                        disabled={submitting || savingDraft || isSubmitDisabled}
                    >
                        {submitting ? "Submitting..." : "Ignite Your Idea"}
                    </Button>
                </div>
            </div>

            <AddTeamMemberDialog
                isOpen={showAddTeamMember}
                onClose={() => setShowAddTeamMember(false)}
                existingMembers={teamMembers}
                onCollect={(member) => {
                    setTeamMembers((prev) => [...prev, member]);
                    setErrors((prev) => {
                        const next = { ...prev };
                        delete next.teamMembers;
                        return next;
                    });
                }}
            />
        </div >
    );
};

export default CreateIdea;

